import {PSEUDO_PREFIX} from '@packages/systems/core/constants/Selectors';
import {
  NO_PSEUDO,
  HOVER,
  ACTIVE,
  VISITED,
  FOCUS,
  FOCUS_VISIBLE,
  FOCUS_WITHIN,
  PLACEHOLDER,
  EMPTY,
  FIRST_CHILD,
  LAST_CHILD,
  ODD_CHILD,
  EVEN_CHILD,
  LANG,
  POPOVER_OPEN,
  BACKDROP,
} from '@packages/systems/core/utils/StyleTypes';

import type {BlockType} from '@packages/systems/core/utils/StyleTypes';

export {
  NO_PSEUDO,
  ODD_CHILD,
  EVEN_CHILD,
  FIRST_CHILD,
  LAST_CHILD,
  HOVER,
  ACTIVE,
  VISITED,
  FOCUS,
  FOCUS_VISIBLE,
  FOCUS_WITHIN,
  PLACEHOLDER,
  LANG,
  POPOVER_OPEN,
  pseudoMap,
  BACKDROP,
} from '@packages/systems/core/utils/StyleTypes';

export const CLASS: BlockType = 'class';
export const TAG: BlockType = 'tag';

// Until https://github.com/facebook/flow/issues/961 lands, we have to inline these string values
export const OPEN = 'open';
export const CURRENT = 'current';

export const PSEUDO_KEYS: [
  typeof NO_PSEUDO,
  typeof ODD_CHILD,
  typeof EVEN_CHILD,
  typeof FIRST_CHILD,
  typeof LAST_CHILD,
  typeof HOVER,
  typeof ACTIVE,
  typeof VISITED,
  typeof FOCUS,
  typeof FOCUS_VISIBLE,
  typeof FOCUS_WITHIN,
  typeof PLACEHOLDER,
  typeof EMPTY,
  typeof LANG,
  typeof POPOVER_OPEN,
  typeof BACKDROP,
] = [
  NO_PSEUDO,
  ODD_CHILD,
  EVEN_CHILD,
  FIRST_CHILD,
  LAST_CHILD,
  HOVER,
  ACTIVE,
  VISITED,
  FOCUS,
  FOCUS_VISIBLE,
  FOCUS_WITHIN,
  PLACEHOLDER,
  EMPTY,
  LANG,
  POPOVER_OPEN,
  BACKDROP,
];

export const FOCUS_COMBO_CLASS = `${PSEUDO_PREFIX}${FOCUS}`;

export const ATOMS_REQUIRING_FOCUS_HACK = [
  'CommerceCheckoutCardSecurityCode',
  'CommerceCheckoutCardExpirationDate',
  'CommerceCheckoutCardNumber',
] as const;

export const CUSTOM_NODE_STATE_KEYS: [typeof OPEN, typeof CURRENT] = [
  OPEN,
  CURRENT,
];

// https://github.com/facebook/fbjs/blob/4369c7dfeb1ab0de27feb3e9245f599c8edd6a5c/packages/fbjs/src/core/hyphenateStyleName.js
const _uppercasePattern = /([A-Z])/g;
function hyphenate(string: string) {
  return string.replace(_uppercasePattern, '-$1').toLowerCase();
}
const msPattern = /^ms-/;
export const hyphenateStyleName = (string: string) => {
  return hyphenate(string).replace(msPattern, '-ms-');
};

/**
 * Appends `!important` to each `;`-separated declaration. Splits on `;` only
 * outside of strings and `()`; invalid or odd input is fine to handle loosely.
 *
 * Idempotent: declarations already marked `!important` are returned unchanged.
 *
 * Element-style rules rely on this to make `@layer elements { ... }` outrank
 * unlayered user CSS, so any consumer that emits element-style declarations
 * must apply the same transformation to keep the cascade contract intact.
 */
export function makeAllImportant(styleText: string): string {
  if (styleText === '') {
    return styleText;
  }

  const parts: string[] = [];
  let start = 0;
  let parenDepth = 0;
  let stringQuote: '"' | "'" | null = null;

  for (let i = 0; i < styleText.length; i++) {
    const ch = styleText[i];
    if (stringQuote) {
      if (ch === '\\' && i + 1 < styleText.length) {
        i++;
      } else if (ch === stringQuote) {
        stringQuote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      stringQuote = ch;
      continue;
    }
    if (ch === '(') {
      parenDepth++;
      continue;
    }
    if (ch === ')') {
      if (parenDepth > 0) parenDepth--;
      continue;
    }
    if (ch === ';' && parenDepth === 0) {
      parts.push(styleText.slice(start, i), ';');
      start = i + 1;
    }
  }
  parts.push(styleText.slice(start));

  return parts
    .map((part) => (part === ';' ? part : appendImportantToDeclaration(part)))
    .join('');
}

function appendImportantToDeclaration(segment: string): string {
  if (segment.trim() === '' || /!\s*important\b/i.test(segment)) {
    return segment;
  }
  return /\s$/.test(segment) ? `${segment}!important` : `${segment} !important`;
}
